import {
  Env,
  ENVS,
  PLATFORMS,
  BC_HOSTNAMES,
  BIM360_HOSTNAMES,
  BIM360_EMEA_HOSTNAMES,
  OXYGEN_URL,
  USER_PROFILE_URL,
  ADMIN_API_URL,
  ADMIN_CONSTRUCTION_API_URL,
  BIM360_ADMIN_URL,
  RELATIONSHIP_API_URL,
  APIGEE_URL,
  REPORTS_V2_URL,
  FORMS_API_URL,
} from './envConsts';
import { parseURL } from './parseURL';

const getSafeEnvFromProcessEnv = () => {
  try {
    return process.env.REACT_APP_ACS_ENV;
  } catch {
    return undefined;
  }
};

const getSafeRegionFromProcessEnv = () => {
  try {
    return process.env.REACT_APP_ACS_REGION;
  } catch {
    return undefined;
  }
};

function isBIM(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_QA(currentHostname: string) {
  return currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]);
}

function isBIM_STAGING(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING])
  );
}

function isBIM_PRODUCTION(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_US(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.QA]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BIM360_HOSTNAMES[ENVS.PRODUCTION])
  );
}

function isBIM_EMEA(currentHostname: string) {
  return (
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.PRODUCTION]) ||
    currentHostname.includes(BIM360_EMEA_HOSTNAMES[ENVS.STAGING])
  );
}

function isBuildingConnected(currentHostname: string) {
  return (
    currentHostname.includes(BC_HOSTNAMES[ENVS.STAGING]) ||
    currentHostname.includes(BC_HOSTNAMES[ENVS.PRODUCTION])
  );
}

/**
 * Returns one of `PLATFORMS` values based on the current hostname.
 * Fallback to `ACC` if not exist.
 */
export function getPlatformFromUrl() {
  if (isBIM(window.location.hostname)) {
    return PLATFORMS.BIM360;
  }

  return PLATFORMS.ACC;
}

let regionFromUrl: string | undefined;

export const getRegionFromUrl = (): string | undefined => {
  if (regionFromUrl) return regionFromUrl;

  const currentHostname = window.location.hostname;

  if (!currentHostname) {
    return regionFromUrl;
  }

  // BuildingConnected and CPA support US region only
  if (isBuildingConnected(currentHostname)) {
    return 'US';
  }

  const platform = getPlatformFromUrl();

  if (platform === PLATFORMS.BIM360) {
    if (isBIM_US(currentHostname)) {
      regionFromUrl = 'US';
    }

    if (isBIM_EMEA(currentHostname)) {
      regionFromUrl = 'EMEA';
      return 'EMEA';
    }
  } else {
    const { region } = parseURL(currentHostname);
    regionFromUrl = region;
    return region;
  }

  return regionFromUrl;
};

export const clearRegionFromUrl = (): void => {
  regionFromUrl = undefined;
};

interface EnvironmentConfig {
  env?: Env;
  region?: string;
}

export const EnvironmentConfigSymbol = Symbol('EnvironmentConfig');

declare global {
  interface Window {
    /** @deprecated */
    REACT_APP_ACS_REGION: string;
    /** @deprecated */
    REACT_APP_ACS_ENV: string;

    [EnvironmentConfigSymbol]?: EnvironmentConfig;
  }
}

/**
 * Returns string of a region according to environment variable or detect it from URL.
 * Fallback to `US` if not exist.
 */
export const getRegion = (): string => {
  const fromWindow = window.REACT_APP_ACS_REGION;
  const fromEnv = getSafeRegionFromProcessEnv();

  return (
    window[EnvironmentConfigSymbol]?.region ??
    (fromWindow || fromEnv || getRegionFromUrl() || 'US').toUpperCase()
  );
};

let envFromUrl: Env | undefined;

const getEnvFromUrl = (): Env | undefined => {
  if (envFromUrl) {
    return envFromUrl;
  }

  const currentHostname = window.location.hostname;
  if (!currentHostname) {
    return envFromUrl;
  }

  if (currentHostname.includes(BC_HOSTNAMES[ENVS.STAGING])) {
    envFromUrl = ENVS.STAGING;
    return ENVS.STAGING;
  }

  if (currentHostname.includes(BC_HOSTNAMES[ENVS.PRODUCTION])) {
    envFromUrl = ENVS.PRODUCTION;
    return ENVS.PRODUCTION;
  }

  const platform = getPlatformFromUrl();
  if (platform === PLATFORMS.BIM360) {
    if (isBIM_QA(currentHostname)) {
      envFromUrl = ENVS.QA;
      return ENVS.QA;
    }

    if (isBIM_STAGING(currentHostname)) {
      envFromUrl = ENVS.STAGING;
      return ENVS.STAGING;
    }

    if (isBIM_PRODUCTION(currentHostname)) {
      envFromUrl = ENVS.PRODUCTION;
      return ENVS.PRODUCTION;
    }
  } else {
    const { env } = parseURL(currentHostname);
    envFromUrl = env;
    return env;
  }

  return envFromUrl;
};

export const clearEnvFromUrl = (): void => {
  envFromUrl = undefined;
};

/**
 * Returns one of `ENVS` values according to environment variable.
 * Fallback to `QA` if not exist.
 */
export const getEnv = (): Env => {
  const fromWindow = window.REACT_APP_ACS_ENV;
  const fromEnv = getSafeEnvFromProcessEnv();

  const env =
    window[EnvironmentConfigSymbol]?.env ??
    (fromWindow || fromEnv || getEnvFromUrl() || ENVS.QA).toUpperCase();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!Object.values(ENVS).includes(env as any)) {
    throw Error(
      `Unknown 'env' value: ${fromEnv}. Must be one of: ${Object.values(ENVS)}`,
    );
  }
  return env as Env;
};

export const getBim360Hostname = ({ region }: overrideProps = {}) => {
  const env = getEnv();
  const finalRegion = region || getRegion();

  // Only US and EMEA are valid regions for BIM 360
  const validRegions = ['US', 'EMEA'];
  if (
    !validRegions.includes(finalRegion) ||
    (env === ENVS.QA && finalRegion !== 'US')
  ) {
    throw new Error(
      `unable to load hostname for region ${finalRegion}, env ${env}`,
    );
  }

  if (finalRegion === 'US') {
    return BIM360_HOSTNAMES[env];
  }

  if (finalRegion === 'EMEA') {
    return BIM360_EMEA_HOSTNAMES[env];
  }

  return BIM360_HOSTNAMES[env];
};

type overrideProps = {
  region?: string;
};

export function getAccHostname({ region }: overrideProps = {}) {
  const env = getEnv();
  const finalRegion = region || getRegion();
  const envPart = env !== ENVS.PRODUCTION ? `-${env}` : '';
  const regionPart = finalRegion !== 'US' ? `${finalRegion}.` : '';

  // QA should support US region only
  if (env === ENVS.QA) {
    return `acc${envPart}.autodesk.com`.toLocaleLowerCase();
  }

  if (
    env === ENVS.STAGING &&
    !['US', 'EMEA', 'APAC', 'AUS'].includes(finalRegion)
  ) {
    return `acc.stg.${regionPart}autodesk.com`.toLocaleLowerCase();
  }

  // EMEA has a different domain
  if (finalRegion === 'EMEA') {
    return `acc${envPart}.autodesk.eu`.toLocaleLowerCase();
  }

  return `acc${envPart}.${regionPart}autodesk.com`.toLocaleLowerCase();
}

export function getCPAHostname({ region }: overrideProps = {}) {
  return getAccHostname({ region }).replace('acc', 'cpa');
}

export const getCPAOrigin = ({ region }: overrideProps = {}) => {
  return `https://${getCPAHostname({ region })}`;
};

export const getAccOrigin = ({ region }: overrideProps = {}) => {
  return `https://${getAccHostname({ region })}`;
};

export const getBim360Origin = ({ region }: overrideProps = {}) => {
  return `https://${getBim360Hostname({ region })}`;
};

export const getLoginUrl = () => {
  const platform = getPlatformFromUrl();

  if (platform === PLATFORMS.BIM360) {
    return `https://login.${getBim360Hostname()}`;
  }

  return `https://login.${getAccHostname()}`;
};

export const getOxygenUrl = () => OXYGEN_URL[getEnv()];
export const getUserProfileUrl = () => USER_PROFILE_URL[getEnv()];
export const getBim360AdminUrl = () => BIM360_ADMIN_URL[getEnv()];
export const getAdminApiUrl = () => ADMIN_API_URL[getEnv()];
export const getAdminConstructionApiUrl = () =>
  ADMIN_CONSTRUCTION_API_URL[getEnv()];
export const getRelationshipServiceUrl = () => RELATIONSHIP_API_URL[getEnv()];
export const getApigeeUrl = () => APIGEE_URL[getEnv()];
export const getReportsV2Url = () => REPORTS_V2_URL[getEnv()];
export const getFormsUrl = () => FORMS_API_URL[getEnv()];
